import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowUpRight, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

const upcomingPosts = [
    { tag: "Discovery", title: "The 7 questions every BANT call is missing", body: "Why even seasoned reps skip the hardest qualification questions — and how to bake them into your call flow." },
    { tag: "Coaching", title: "How to run a 30-minute 1:1 that actually changes behaviour", body: "Three coaching frameworks the best sales managers use, mapped to PipeGuide's category breakdowns." },
    { tag: "Playbooks", title: "BANT vs MEDDIC vs your own framework — when to use which", body: "A practical decision tree for sales teams qualifying mid-market and enterprise deals." },
];

export default function Blog() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setSubmitting(true);
        try {
            await api.post("/newsletter", { email: email.trim() });
            toast.success("You're on the list — we'll email you the first post.");
            setEmail("");
        } catch (err) {
            toast.error("Something went wrong. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="blog-page" className="min-h-screen flex flex-col bg-white">
            <div className="bg-white border-b border-zinc-200">
                <Navbar tone="light" />
            </div>

            <section className="bg-gradient-to-b from-zinc-50 to-white border-b border-zinc-200">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 py-16 md:py-20">
                    <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs">
                        <BookOpen className="h-3.5 w-3.5 text-brand-primary" />
                        <span className="text-zinc-700">Coming soon</span>
                    </div>
                    <h1 className="mt-5 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">
                        Field notes from real sales floors.
                    </h1>
                    <p className="mt-4 text-lg text-zinc-600 max-w-xl">
                        Practical playbooks, qualification deep-dives and coaching templates — written for sales teams that want to close more.
                    </p>

                    <form onSubmit={subscribe} data-testid="blog-newsletter-form" className="mt-8 flex flex-col sm:flex-row gap-2 max-w-md">
                        <div className="relative flex-1">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@company.com"
                                data-testid="blog-newsletter-email"
                                className="w-full h-11 rounded-lg bg-white border border-zinc-300 pl-9 pr-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                            />
                        </div>
                        <Button type="submit" disabled={submitting} data-testid="blog-newsletter-submit" className="bg-brand-primary hover:bg-brand-hover text-white h-11 px-5">
                            {submitting ? "..." : "Notify me"}
                        </Button>
                    </form>
                </div>
            </section>

            <section className="bg-white flex-1">
                <div className="max-w-5xl mx-auto px-6 sm:px-8 py-16">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-6">Up next</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {upcomingPosts.map((p, i) => (
                            <article key={i} data-testid={`blog-upcoming-${i}`} className="rounded-2xl border border-zinc-200 bg-zinc-50/50 p-6">
                                <span className="inline-flex items-center rounded-full bg-brand-tint text-brand-hover px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">{p.tag}</span>
                                <h3 className="mt-4 font-display text-lg font-semibold text-zinc-900 leading-snug">{p.title}</h3>
                                <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{p.body}</p>
                            </article>
                        ))}
                    </div>

                    <div className="mt-12 rounded-2xl border border-zinc-200 bg-zinc-50/50 p-7 text-center">
                        <p className="text-sm text-zinc-600">Want to see a topic covered first?</p>
                        <Link to="/contact" className="mt-2 inline-flex items-center text-sm font-medium text-brand-primary hover:underline">
                            Suggest a topic <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
